/**
 * Translation, as a lookup and nothing more.
 *
 * A catalogue is a flat map of key to message — flat because a missing key is
 * then a missing line in a diff rather than a hole three levels down a tree,
 * and because `en` and `sr` can be compared key for key in a test.
 *
 * A message is either a string, or an object of plural forms picked by `count`.
 * Placeholders are `{named}` and are substituted verbatim; nothing here builds
 * HTML, so nothing here can inject any.
 *
 * English is the fallback for everything. A language that has not translated a
 * line shows the English one rather than the key: a word in the wrong language
 * is a blemish, a key on screen is a bug someone has to report.
 */

/**
 * Which plural form a count takes, per language.
 *
 * English has two. Serbian has three, and they are not "singular, dual,
 * plural": 21 takes the same form as 1, and 22 the same as 2, so the rule is
 * about the last digit — with the teens carved out, because 11 and 12 do not
 * follow it. These are the CLDR rules, written out rather than pulled in.
 */
const PLURALS = {
  en: (n) => (n === 1 ? 'one' : 'other'),
  sr: (n) => {
    const last = n % 10
    const tens = n % 100
    if (last === 1 && tens !== 11) return 'one'
    if (last >= 2 && last <= 4 && (tens < 12 || tens > 14)) return 'few'
    return 'other'
  },
}

export const pluralForm = (locale, count) => (PLURALS[locale] ?? PLURALS.en)(Math.abs(count))

/** `{name}` → the value given for `name`. An absent value leaves the text. */
export function interpolate(message, params = {}) {
  if (!message.includes('{')) return message
  return message.replace(/\{(\w+)\}/g, (whole, name) =>
    params[name] === undefined || params[name] === null ? whole : String(params[name]),
  )
}

/**
 * Looks `key` up in `catalogue`, falling back to `fallback`, and fills it in.
 *
 * @param {object} o
 * @param {object} o.catalogue  the active language
 * @param {object} [o.fallback] English, normally
 * @param {string} [o.locale]   whose plural rules to use
 * @param {(key: string) => void} [o.onMissing]
 * @returns {string} the finished line, or the key itself if nothing has it
 */
export function translate(key, params = {}, { catalogue, fallback, locale = 'en', onMissing } = {}) {
  let message = catalogue?.[key]
  if (message === undefined) {
    message = fallback?.[key]
    if (message !== undefined) onMissing?.(key)
  }
  if (message === undefined) {
    onMissing?.(key)
    return key
  }
  if (typeof message === 'object') {
    const count = Number(params.count) || 0
    message =
      message[pluralForm(locale, count)] ?? message.other ?? message.one ?? String(key)
  }
  return interpolate(String(message), params)
}

/**
 * An error, said in a language. Code that cannot hold words (lib/, drivers,
 * the server) gives an error meant for a person a `code` — and `params` if it
 * needs them — keeping its English message for the console. A code the
 * catalogues know (`error.<code>`) is translated; anything else is shown as it
 * came, which for a server's detailed refusal is better than a vague one.
 */
export function errorMessage(err, { catalogue, fallback, locale } = {}) {
  const key = err?.code ? `error.${err.code}` : null
  if (key && (catalogue?.[key] !== undefined || fallback?.[key] !== undefined)) {
    return translate(key, err.params ?? {}, { catalogue, fallback, locale })
  }
  return err?.message ?? String(err ?? '')
}

/** Every key one catalogue has and the other does not, both ways round. */
export function compareCatalogues(a, b) {
  const keysOf = (c) => new Set(Object.keys(c ?? {}))
  const left = keysOf(a)
  const right = keysOf(b)
  return {
    missing: [...left].filter((k) => !right.has(k)).sort(),
    extra: [...right].filter((k) => !left.has(k)).sort(),
  }
}
